import { Confirmation } from '@abp/ng.theme.shared';
import { inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { map } from 'rxjs/operators';
import { ConfirmationService } from 'src/core/services/confirmation/confirmation.service';
import { WallboardMainComponent } from '../components/wallboard-main/wallboard-main.component';

/**
 * Asks before leaving the wallboard with unsaved layout changes.
 *
 * Reads {@link WallboardMainComponent.dirty} straight off the component instance the router hands over,
 * which is why the page keeps that flag public: dirtiness is one fact, and a guard that kept its own copy
 * would be able to disagree with the toolbar about whether there is anything to lose.
 *
 * A clean board returns `true` synchronously and shows nothing — the confirmation must never appear on a
 * board the user only looked at. Cancelling returns `false`, which leaves the router on the wallboard with
 * the edits intact.
 *
 * The same question is asked in one other place, by the page itself, before a board switch throws the
 * edits away. The two cannot be merged: this one is about a URL change and the router owns the answer,
 * that one is about replacing the layout in place.
 */
export const wallboardUnsavedChangesGuard: CanDeactivateFn<WallboardMainComponent> = component => {
  // `inject` before the early return: it is only legal for as long as the guard's own synchronous
  // execution is in an injection context.
  const confirmationService = inject(ConfirmationService);

  if (!component.dirty) {
    return true;
  }

  return confirmationService
    .info('Dashboard::WallboardUnsavedChangesMessage', 'Dashboard::WallboardUnsavedChangesTitle')
    .pipe(map(status => status === Confirmation.Status.confirm));
};
